import React, { Fragment, useMemo, useState } from "react";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { buildRevisionTimeline } from "@/lib/revisionTimeline";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");
const fmtQty = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());
const CHANGE_CLS = { increase: "text-amber-700", decrease: "text-emerald-700", removed: "text-rose-700" };

/** "Revision History" button — opens a dialog listing every revision made to
 * a Transfer Order / Sales Order: revision no, qty and delivery-date change
 * (overall and per line), when, who and why. */
export default function RevisionHistoryButton({ record, baseNo, testid = "revision-history" }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => buildRevisionTimeline(record, baseNo), [record, baseNo]);
  if (!record) return null;
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`${testid}-btn`}>
        <History className="w-4 h-4 mr-1.5" /> Revision History{rows.length ? ` (${rows.length})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Revision History — {baseNo}</DialogTitle>
          </DialogHeader>
          {rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center" data-testid={`${testid}-empty`}>
              No revisions have been made to {baseNo} yet.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="py-2 px-3">Revision</th>
                    <th className="py-2 px-3">Qty</th>
                    <th className="py-2 px-3">Delivery Date</th>
                    <th className="py-2 px-3">Revised On / By</th>
                    <th className="py-2 px-3">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((h) => (
                    <Fragment key={h.revision_number}>
                      <tr className="border-t border-slate-200 align-top bg-white" data-testid={`${testid}-row-${h.revision_number}`}>
                        <td className="py-2 px-3 mono font-semibold whitespace-nowrap">
                          {h.revision_no}
                          <div className="text-[10px] font-normal text-slate-400">of {h.original_no}</div>
                        </td>
                        <td className="py-2 px-3 mono whitespace-nowrap">
                          {h.incomplete ? "—" : <>{fmtQty(h.previous_qty)} → {fmtQty(h.revised_qty)}</>}
                          {h.qty_change && h.qty_change !== "unchanged" && (
                            <span className={`ml-1 ${CHANGE_CLS[h.qty_change] || ""}`}>({h.qty_change})</span>
                          )}
                        </td>
                        <td className="py-2 px-3 mono whitespace-nowrap">
                          {h.incomplete ? "—" : <>{fmtDate(h.previous_delivery_date)} → {fmtDate(h.revised_delivery_date)}</>}
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {h.revised_at ? new Date(h.revised_at).toLocaleString() : "—"}
                          <div className="text-[10px] text-slate-400">{h.revised_by || ""}</div>
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {h.incomplete ? "Details not recorded for this revision" : h.reason || "—"}
                        </td>
                      </tr>
                      {(h.lines || []).length > 0 && (
                        <tr className="bg-slate-50/60">
                          <td />
                          <td colSpan={4} className="pb-2 px-3">
                            <table className="w-full text-[11px] text-slate-600">
                              <tbody>
                                {h.lines.map((l, i) => (
                                  <tr key={i}>
                                    <td className="py-0.5 pr-3 mono w-40">{l.part_code || "—"}</td>
                                    <td className="py-0.5 pr-3 mono">
                                      Qty {fmtQty(l.previous_qty)} → {fmtQty(l.revised_qty)}
                                      {l.rule === "removed" && <span className={`ml-1 ${CHANGE_CLS.removed}`}>(removed)</span>}
                                    </td>
                                    <td className="py-0.5 mono">Delivery {fmtDate(l.previous_delivery_date)} → {fmtDate(l.revised_delivery_date)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
